import {
  MouseSensor,
  SensorProps,
  TouchSensor,
  TouchSensorOptions,
  useSensor,
  useSensors,
} from '@dnd-kit/core'

/*
 * What a finger presses to start a drag. A whole card or row sits in a scrollable list, so its hold
 * has to outlast a pause before a swipe (400ms, the platform long press: 400ms on Android, 500ms on
 * iOS). A handle is an explicit grip and keeps a short hold.
 */
export type DragActivator = 'item' | 'handle'

const TOUCH_DELAY: Record<DragActivator, number> = {
  item: 400,
  handle: 100,
}

/*
 * @dnd-kit/core 6.0.5 TouchSensor ignores `touchcancel`: a touch the system takes away (an iOS edge
 * gesture, an alert) still activates when the hold timer fires, and an active drag waits for the
 * next `touchend` on its target. `handleCancel` is private in the typings but is the sensor's own
 * cancel path. Drop this subclass once the pinned version listens for `touchcancel` itself.
 */
export class CancelableTouchSensor extends TouchSensor {
  constructor(props: SensorProps<TouchSensorOptions>) {
    let stopListening = () => {}
    super({
      ...props,
      onEnd: () => {
        stopListening()
        props.onEnd()
      },
      onCancel: () => {
        stopListening()
        props.onCancel()
      },
    })
    const { target } = props.event
    const cancel = () => (this as unknown as { handleCancel: () => void }).handleCancel()
    target?.addEventListener('touchcancel', cancel)
    stopListening = () => target?.removeEventListener('touchcancel', cancel)
  }
}

/*
 * Touch never goes through PointerSensor: in @dnd-kit/core 6.0.5 it ignores `pointercancel`, so a
 * drag started by a scroll swipe stays active after the browser takes over the touch and drops on
 * the next tap anywhere. The 5px tolerance is below the browser's touch slop, so a moving finger
 * cancels the hold before native scrolling starts.
 */
export const useDragSensors = (activator: DragActivator) =>
  useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(CancelableTouchSensor, {
      activationConstraint: {
        delay: TOUCH_DELAY[activator],
        tolerance: 5,
      },
    }),
  )

/*
 * For an `item` activator: on a touch screen its long press would otherwise also select text or open
 * the iOS callout. Scoped to coarse pointers so a mouse can still double-click to select text.
 */
export const LONG_PRESS_DRAG_CLASS_NAME =
  'pointer-coarse:select-none pointer-coarse:[-webkit-touch-callout:none]'
